/**
 * The order of the guide's category list: USA first, the most-watched kinds of
 * USA category at the top, for ANY provider's naming (owner, 2026-09-26/27:
 * "all usa channels first and usa ultra hd first"; "every provider will have
 * their own naming nomenclature — rules to make the most popular US categories
 * at the top for any provider"). Providers scatter their USA categories through
 * the list (positions 6 to 128 of 131 on the owner's), so a viewer scrolled past
 * leagues and 24/7 loops to find the channels they watch.
 *
 * By what a category IS, read from the words in its name, never from exact
 * names: "Usa Espn", "US| SPORTS", "USA - Sports", "[US] NFL", "United States
 * Locals" all land where they should. Within a kind, and for everything not
 * USA, the provider's own order is kept.
 *
 * After USA come the categories with no country ("24/7 Drama", "Live Football"),
 * then other countries' ("Uk Dazn", "Australia Kayo") last (owner, 2026-10-09).
 *
 * "Your country" (owner, 2026-10-20): a viewer outside the US gets their own country's
 * categories first instead, Ultra HD first among them and otherwise in the provider's own
 * order (the kinds are tuned for US names only), then no country, then the rest, the US's
 * among them. For a US viewer nothing changes.
 */
import { isOtherCountry, isViewerCountry } from "../library/countryTag.js";

/** A USA category, however the provider spells it. "us" alone counts only at the start ("Kids & Us" is not). */
const USA = /^[\s|[(#:-]*(usa|us|united states)(?![a-z])|(^|[^a-z])usa(?![a-z])/i;

/** Whether a category or channel name is tagged USA, however the provider spells it. */
export const isUsaName = (name: string): boolean => USA.test(name);

const word = (pattern: string) => new RegExp(`(^|[^a-z0-9])(${pattern})(?![a-z])`, "i");

/**
 * The kinds, most-watched first. Checked in the order listed in `kindOf`, not
 * this one: a niche package ("NFL Teams", "NBA Replay") must be caught before
 * its sport, and a sports network before the broadcaster in its name
 * ("Fox Sports", "NBC Sports", "Disney Sports").
 */
const KINDS = {
  ultraHd: 0,
  entertainment: 1,
  sports: 2,
  locals: 3,
  news: 4,
  movies: 5,
  kids: 6,
  lifestyle: 7,
  other: 8,
  latin: 9,
  niche: 10,
} as const;

const ULTRA_HD = /ultra\s*hd|(^|[^a-z0-9])(uhd|4k)(?![a-z])/i;
/** Collections and packages rather than a channel lineup: streaming originals, 24/7 loops, replays, team packages, events, free-streaming apps. */
const NICHE = word(
  "originals?|series|24\\s*\\/?\\s*7|vod|replays?|teams?|game\\s*pass|pass|events?|ppv|pay\\s*per\\s*view|plex|tubi|samsung|pluto|roku|xumo|fast|flo|milb|ahl|nll|softball|cheer|dirtvision|appletv|apple\\s*tv|netflix|hulu|hbomax|primevideo|prime\\s*video|peacock",
);
const SPORTS = word("sports?|espn\\w*|nfl|nba|wnba|mlb|nhl|mls|ncaa\\w*|college|btn|sec|acc|accnx|big\\s*ten|golf|tennis|boxing|ufc|wwe|racing|nascar|f1|soccer|football|basketball|baseball|hockey|dazn|fubo|bein");
const LOCALS = word("locals?|abc|cbs|nbc|fox|cw|pbs|my\\s*network|networks?");
const NEWS = word("news");
const ENTERTAINMENT = word("entertainment|general|cable|variety|channels|fhd|hd|sd");
const MOVIES = word("movies?|cinema\\w*|films?|hbo|cinemax|showtime|starz|epix|mgm\\+?|premium");
const KIDS = word("kids?|family|cartoons?|children|junior|nick\\w*|disney");
const LIFESTYLE = word("documentar\\w*|lifestyle|music|religio\\w*|faith|christian|shopping|weather|travel|food|cooking|history|science|nature");
const LATIN = word("latin\\w*|spanish|espa(n|ñ)ol|hispan\\w*|univision|telemundo|unimas|vix|mexic\\w*");

function kindOf(name: string): number {
  if (!USA.test(name)) return Number.POSITIVE_INFINITY;
  if (ULTRA_HD.test(name)) return KINDS.ultraHd;
  if (NICHE.test(name)) return KINDS.niche;
  if (LATIN.test(name)) return KINDS.latin;
  if (SPORTS.test(name)) return KINDS.sports;
  if (NEWS.test(name)) return KINDS.news;
  if (MOVIES.test(name)) return KINDS.movies;
  if (KIDS.test(name)) return KINDS.kids;
  if (LIFESTYLE.test(name)) return KINDS.lifestyle;
  if (LOCALS.test(name)) return KINDS.locals;
  if (ENTERTAINMENT.test(name)) return KINDS.entertainment;
  return KINDS.other;
}

/** A category of the viewer's own country outside the US: Ultra HD first, the rest in the provider's order. */
function ownKindOf(name: string, viewer: string): number {
  if (!isViewerCountry(name, viewer)) return Number.POSITIVE_INFINITY;
  return ULTRA_HD.test(name) ? KINDS.ultraHd : KINDS.entertainment;
}

/**
 * `categories` in guide order: the viewer's country first (for the US, by kind, most-watched
 * first), then no country, then other countries; stable within each.
 */
export function orderCategories<T extends { name: string }>(categories: readonly T[], viewer = "US"): T[] {
  const tierOf = (name: string, kind: number) => (Number.isFinite(kind) ? 0 : isOtherCountry(name, viewer) ? 2 : 1);
  return categories
    .map((category, index) => {
      const kind = viewer === "US" ? kindOf(category.name) : ownKindOf(category.name, viewer);
      return { category, index, kind, tier: tierOf(category.name, kind) };
    })
    .sort((a, b) => a.tier - b.tier || a.kind - b.kind || a.index - b.index)
    .map((entry) => entry.category);
}

/**
 * The category the guide opens on at app start and after a profile switch
 * (owner, 2026-09-29): the profile's ★ Favorites when it has any, else the
 * viewer's own saved pick, else the first in `orderCategories` (USA Ultra HD
 * first). Within a session the guide keeps whatever it was on (2026-09-25).
 */
export function openingCategory(input: {
  favoritesId: string;
  favoriteCount: number;
  saved: string | null;
  chosen: boolean;
  /** Whether `saved` still exists (a provider change can remove it). */
  savedExists: boolean;
  first: string | null;
}): string | null {
  if (input.favoriteCount > 0) return input.favoritesId;
  const savedUsable = input.chosen && input.saved !== input.favoritesId && (input.saved === null || input.savedExists);
  return savedUsable ? input.saved : input.first;
}
